import React, { useState } from 'react';

import classNames from 'classnames';
import { Col, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faArrowCircleLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SplitPane from 'react-split-pane';
import Swal from 'sweetalert2';
import { useHistory, useParams } from 'react-router-dom';

import ActionDropDown from 'components/shared/ActionDropDown';
import { areYouSure } from 'lib/utils/toast';
import GDSNSubscriptionModal from 'components/manage_manufacturers/manufacturer_details/GDSNSubscriptionModal';

import Container from 'components/shared/Container';
import {
  getURL,
  CLIENT_GLOBAL_MANUFACTURER,
  GLOBAL_MANUFACTURERS_BROKEN_IMAGE_DELETE,
  GLOBAL_MANUFACTURERS_CONVERT_TO_DISTRIBUTOR,
  GLOBAL_MANUFACTURERS_GDSN_SUBSCRIPTION,
  GLOBAL_MANUFACTURERS_GET_BY_ID,
  GLOBAL_MANUFACTURERS_UPDATE_FROM_FSA,
  GLOBAL_MANUFACTURERS_EXPORT_BRAND_PRODUCTS,
  FILE_EXPORTS_MANUFACTURER_SKU_CLEAN_UP_REPORT,
} from 'lib/networking/endpoints';
import { CUT_DRY_CLIENT_ID } from 'lib/constants';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import LogoUpdateModal from 'components/shared/LogoUpdateModal';
import ManufacturerMenuSplitView from 'components/manage_manufacturers/manufacturer_details/ManufacturerMenuSplitView';
import ManufacturerLogoSplitView from 'components/manage_manufacturers/manufacturer_details/ManufacturerLogoSplitView';
import pointerOnHover from 'lib/css/pointerOnHover';
import SwalDialog from 'lib/utils/toast';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import usePut from 'lib/hooks/usePut';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';

function ManufacturerDetailsView() {
  const { manufacturerID } = useParams();
  const history = useHistory();
  const hasUserPermissions = useUserPermissions();

  const [showManufacturerLogoModal, setShowManufacturerLogoModal] = useState(false);
  const [showGDSNModal, setShowGDSNModal] = useState(false);

  const {
    data: { manufacturer } = {},
    loading,
    error,
    refetch,
  } = useGet(
    getURL(GLOBAL_MANUFACTURERS_GET_BY_ID, { manufacturer_id: manufacturerID }),
    {},
    !manufacturerID,
  );

  const { postData: updateManufacturer, loading: updateLoading } = usePost(
    GLOBAL_MANUFACTURERS_UPDATE_FROM_FSA,
    () => {
      SwalDialog('success', 'Successfully updated manufacturer', 'Success', 'center', refetch());
    },
    error => {
      SwalDialog(
        'error',
        `An error occurred while updating the manufacturer: ${error?.response?.data?.message}`,
        'Error',
        'center',
      );
    },
  );

  const { postData: deleteBrokenImages } = usePost(
    getURL(GLOBAL_MANUFACTURERS_BROKEN_IMAGE_DELETE, { manufacturer_id: manufacturerID }),
    () =>
      Swal.fire({
        title: 'Success',
        text: `Started removing broken image links of ${manufacturer.name}.`,
        icon: 'success',
      }),
    error =>
      Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || 'Something went wrong, please try again.',
        icon: 'error',
      }),
  );

  const { postData: convertToDistributor } = usePost(
    getURL(GLOBAL_MANUFACTURERS_CONVERT_TO_DISTRIBUTOR, { manufacturer_id: manufacturerID }),
    () =>
      Swal.fire({
        title: 'Success',
        text: `Started converting products of ${manufacturer.name} to distibutor products.`,
        icon: 'success',
      }),
    error =>
      Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || 'Something went wrong, please try again.',
        icon: 'error',
      }),
  );

  const { postData: sendGDSNSubscriptionMessage } = usePost(
    getURL(GLOBAL_MANUFACTURERS_GDSN_SUBSCRIPTION, { manufacturer_id: manufacturerID }),
    () =>
      Swal.fire({
        title: 'Success',
        text: `Successfully subscribed to GDSN service.`,
        icon: 'success',
      }),
    error =>
      Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || 'Something went wrong, please try again.',
        icon: 'error',
      }),
  );

  const { postData: exportBrandProducts } = usePost(
    getURL(GLOBAL_MANUFACTURERS_EXPORT_BRAND_PRODUCTS, { manufacturer_id: manufacturerID }),
    () => {
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The export csv file will be generated in a while. 
          You can download the generated file from\n
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Success',
        position: 'center',
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: `An error occurred on exporting updated products: ${error?.response?.data?.message}`,
        title: 'Error',
      }),
  );

  const { postData: exportSkuCleanUpReport } = usePost(
    getURL(FILE_EXPORTS_MANUFACTURER_SKU_CLEAN_UP_REPORT, { manufacturer_id: manufacturerID }),
    () => {
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The export csv file will be generated in a while. 
          You can download the generated file from\n
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Success',
        position: 'center',
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: `An error occurred on exporting: ${error?.response?.data?.message}`,
        title: 'Error',
      }),
  );

  const { data: clientGlobalManufacturerData, refetch: refetchClientGlobalManufacturer } = useGet(
    CLIENT_GLOBAL_MANUFACTURER,
    {
      client_id: CUT_DRY_CLIENT_ID,
      global_manufacturer_id: manufacturerID,
    },
    !manufacturerID,
  );

  const { putData: updateClientGlobalManufacturer } = usePut(
    CLIENT_GLOBAL_MANUFACTURER,
    () => {
      SwalDialog(
        'success',
        'Successfully updated manufacturer',
        'Success',
        'center',
        refetchClientGlobalManufacturer(),
      );
    },
    error => {
      SwalDialog(
        'error',
        `An error occurred while updating the manufacturer: ${error?.response?.data?.message}`,
        'Error',
        'center',
      );
    },
  );

  const handleGDSNSubscription = gln => {
    sendGDSNSubscriptionMessage({ gln });
  };

  if (loading || updateLoading) {
    return <LoadingSpinner />;
  }

  if (error || !manufacturer) {
    return null;
  }

  return (
    <Container>
      <Row>
        <Col xs={4}>
          <h3 className="font-weight-bold">
            <FontAwesomeIcon
              className={classNames(pointerOnHover, 'mr-2')}
              icon={faArrowCircleLeft}
              onClick={() => history.push('/enrich-products/manage-manufacturers')}
            />
            All Manufacturers
          </h3>
        </Col>
        <Col className="d-flex mr-5 justify-content-end">
          <ActionDropDown
            submenuItems={[
              {
                title: 'Remove Broken images',
                action: () => deleteBrokenImages(),
              },
              {
                title: 'Convert manufacturer products to distributor products',
                action: () =>
                  areYouSure(
                    () => convertToDistributor(),
                    'Are you sure you want to covert connected products to distributor products?',
                  ),
                disabled: !hasUserPermissions([UserPermission.CONVERT_MANUFACTURER_PRODUCTS]),
              },
              {
                title: 'Subscribe to GDSN sync',
                action: () => setShowGDSNModal(true),
                disabled:
                  !manufacturer.manufacturer_glns ||
                  Object.keys(manufacturer.manufacturer_glns).length === 0,
              },
              {
                title: 'Export Brand Products',
                action: () => exportBrandProducts(),
                disabled: !hasUserPermissions([UserPermission.ENRICH_PRODUCTS]),
              },
              {
                title: 'Export SKU Clean Up Report',
                action: () => exportSkuCleanUpReport(),
                disabled:
                  !hasUserPermissions([UserPermission.ENRICH_PRODUCTS]) ||
                  !hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA]),
              },
            ]}
          />
        </Col>
      </Row>
      <SplitPane
        primary="first"
        split="vertical"
        defaultSize="25%"
        className={css(styles.splitPane)}
        allowResize={false}
      >
        <ManufacturerLogoSplitView
          manufacturer={manufacturer}
          updateManufacturer={updateManufacturer}
          setShowManufacturerLogoModal={setShowManufacturerLogoModal}
        />
        <ManufacturerMenuSplitView
          manufacturer={manufacturer}
          updateManufacturer={updateManufacturer}
          refetch={refetch}
          clientGlobalManufacturerData={clientGlobalManufacturerData}
          updateClientGlobalManufacturer={updateClientGlobalManufacturer}
        />
      </SplitPane>
      {showManufacturerLogoModal ? (
        <LogoUpdateModal
          product={manufacturer}
          update={updateManufacturer}
          onHide={() => {
            setShowManufacturerLogoModal(false);
            refetch();
          }}
          loading={updateLoading}
        />
      ) : null}
      <GDSNSubscriptionModal
        show={showGDSNModal}
        onHide={() => setShowGDSNModal(false)}
        manufacturer={manufacturer}
        onSubscribe={handleGDSNSubscription}
      />
    </Container>
  );
}

const styles = StyleSheet.create({
  splitPane: {
    position: 'relative',
    minHeight: '80vh',
  },
});

export default ManufacturerDetailsView;
